/**
 * Questionnaires API — typed wrapper around /v3/questionnaires endpoints.
 * Legacy questionnaires are stored separately from Tests (quizzes).
 */
import apiClient from './client';

export interface LegacyQuestionnaire {
  id: string;
  name?: string;
  description?: string;
  access?: string;
  ownerId?: string;
  copyrightDescription?: string;
  studentTask?: string;
  questions?: LegacyQuestion[];
  createdTimestamp?: number;
  [key: string]: any;
}

export interface LegacyQuestion {
  id: string;
  question?: string;
  ruQuestion?: string;
  url?: string;          // video url in legacy format
  answers?: LegacyAnswer[];
  [key: string]: any;
}

export interface LegacyAnswer {
  id?: string;
  answer?: string;
  ruAnswer?: string;
  audioUrl?: string;
  [key: string]: any;
}

const QuestionnairesAPI = {
  /** Get all questionnaires */
  async getAll(): Promise<LegacyQuestionnaire[]> {
    const { data } = await apiClient.get<any>('/v3/questionnaires/all');
    const list = (data as any)?.result ?? data ?? [];
    return Array.isArray(list) ? list : [];
  },

  /** Get a single questionnaire by ID */
  async getById(id: string): Promise<LegacyQuestionnaire> {
    const { data } = await apiClient.get<any>(`/v3/questionnaire/${id}`);
    return (data as any)?.result ?? data;
  },

  /** Create a new questionnaire */
  async create(payload: Partial<LegacyQuestionnaire>): Promise<LegacyQuestionnaire> {
    const { data } = await apiClient.post<any>('/v3/questionnaires', payload);
    return (data as any)?.result ?? data;
  },

  /** Update an existing questionnaire */
  async update(payload: Partial<LegacyQuestionnaire> & { id: string }): Promise<LegacyQuestionnaire> {
    const { data } = await apiClient.put<any>('/v3/questionnaires', payload);
    return (data as any)?.result ?? data;
  },

  /** Delete a questionnaire (moves to trash) */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`/v3/questionnaires/${id}`);
  },

  /** Get questionnaires in trash */
  async getTrash(): Promise<LegacyQuestionnaire[]> {
    const { data } = await apiClient.get<any>('/v3/questionnaires/trash');
    const list = (data as any)?.result ?? data ?? [];
    return Array.isArray(list) ? list : [];
  },

  /** Restore a questionnaire from trash */
  async restore(id: string): Promise<void> {
    await apiClient.post(`/v3/questionnaires/${id}/restore`, {});
  },

  /** Permanently delete a questionnaire */
  async permanentDelete(id: string): Promise<void> {
    await apiClient.delete(`/v3/questionnaires/${id}/permanent`);
  },

  /** Clone a questionnaire — creates a copy with '(копия)' appended to the name */
  async clone(id: string): Promise<LegacyQuestionnaire> {
    const original = await this.getById(id);
    const { id: _id, createdTimestamp: _cts, ...rest } = original as any;
    return this.create({ ...rest, name: `${original.name ?? ''} (копия)` });
  },

  /** Save student's selected answer IDs for a questionnaire */
  async saveAnswer(questionnaireId: string, userId: string, selectedAnswers: string[]): Promise<void> {
    await apiClient.post(`/v3/questionnaire/${questionnaireId}/answer/${userId}`, { selectedAnswers });
  },

  /** Load student's saved answers for a questionnaire */
  async getAnswer(questionnaireId: string, userId: string): Promise<{ selectedAnswers?: string[] }> {
    try {
      const { data } = await apiClient.get<{ selectedAnswers?: string[] }>(
        `/v3/questionnaire/${questionnaireId}/answer/${userId}`,
      );
      return data ?? {};
    } catch {
      return {};
    }
  },

  /**
   * Save a student's Phase-3 (drills) audio recording URL for one question.
   * Primary: localStorage (instant, no network).
   * Primary: backend /v3/questionnaire-recordings.
   * Secondary: localStorage (offline/fast cache).
   */
  async saveRecording(questionnaireId: string, userId: string, questionId: string, audioUrl: string): Promise<void> {
    // 1. Persist to localStorage immediately — fast local cache
    try {
      const key = `ep_qrec_${questionnaireId}_${userId}`;
      const existing = JSON.parse(localStorage.getItem(key) ?? '{}');
      existing[questionId] = audioUrl;
      localStorage.setItem(key, JSON.stringify(existing));
    } catch { /* storage full — ignore */ }

    // 2. Save to backend (primary cross-device storage)
    try {
      await apiClient.post('/v3/questionnaire-recordings', { questionnaireId, userId, questionId, audioUrl });
    } catch { /* backend sync failed — localStorage is enough for this session */ }
  },

  /**
   * Load all drill recording URLs saved by a student for a questionnaire.
   * Merges localStorage (fast) + Parse (cross-device).
   */
  async getRecordings(questionnaireId: string, userId: string): Promise<Record<string, string>> {
    // 1. Start with localStorage (instant)
    let local: Record<string, string> = {};
    try {
      const key = `ep_qrec_${questionnaireId}_${userId}`;
      local = JSON.parse(localStorage.getItem(key) ?? '{}');
    } catch { /* ignore */ }

    // 2. Enrich from backend (cross-device source of truth)
    try {
      const res = await apiClient.get<{ result: Array<{questionId: string; audioUrl?: string}> }>(
        '/v3/questionnaire-recordings',
        { params: { questionnaireId, userId } },
      );
      const rows = res.data?.result ?? [];
      // Merge backend into local — backend wins for cross-device
      for (const row of rows) {
        if (row.questionId && row.audioUrl) local[row.questionId] = row.audioUrl;
      }
      // Sync merged result back to localStorage
      try {
        const key = `ep_qrec_${questionnaireId}_${userId}`;
        localStorage.setItem(key, JSON.stringify(local));
      } catch { /* ignore */ }
    } catch { /* use localStorage only */ }

    return local;
  },
};

export default QuestionnairesAPI;
